import React, { useState } from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import consentService from '../Services/consentService';
import { toast } from 'sonner';

const HealthDataConsent = ({ userId, onClose }) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeclineWarning, setShowDeclineWarning] = useState(false);

  const handleAccept = async () => {
    if (!accepted) {
      alert('Vous devez cocher la case pour continuer');
      return;
    }

    setLoading(true);

    try {
      await consentService.saveHealthDataConsent(userId);
      toast.success('Consentement enregistré');

      if (onClose) onClose();
    } catch (error) {
      console.error('Erreur consentement:', error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setShowDeclineWarning(true);
  };

  const handleConfirmDecline = () => {
    window.location.href = '/';
  };

  const handleCancelDecline = () => {
    setShowDeclineWarning(false);
  };

  // Modal de refus
  if (showDeclineWarning) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Fonctionnalité indisponible
            </h3>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Sans votre consentement, vous ne pourrez pas utiliser l'assistant médical IA. Cette
              fonctionnalité nécessite le traitement de données de santé conformément au RGPD.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-6">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              💡 <strong>Bon à savoir :</strong> Vous pourrez accepter ultérieurement depuis vos
              paramètres de compte.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleCancelDecline}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Relire les informations
            </button>

            <button
              onClick={handleConfirmDecline}
              className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Retourner à l'accueil
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            Les autres fonctionnalités de Remadia restent accessibles
          </p>
        </div>
      </div>
    );
  }

  // Modal principale
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full">
        {/* Header */}
        <div className="bg-blue-600 p-6 rounded-t-lg">
          <div className="flex items-center gap-3 text-white">
            <Shield className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Protection de vos données de santé</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Important notice */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white mb-2">
                  Données sensibles (Article 9 RGPD)
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Les conversations avec notre assistant IA peuvent contenir des{' '}
                  <strong>données de santé sensibles</strong> qui bénéficient d'une protection
                  renforcée.
                </p>
              </div>
            </div>
          </div>

          {/* Protection measures */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Comment nous protégeons vos données :
            </h3>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Chiffrement AES-256-GCM côté serveur</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Clé de chiffrement individuelle par utilisateur</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Conservation limitée à 2 ans maximum</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Suppression automatique à expiration</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>
                  Transmises à OpenAI, L.L.C. (États-Unis) pour générer les réponses, avec le
                  paramètre store=false : ni conservation durable, ni entraînement des modèles.
                  Transfert encadré par un DPA et les Clauses Contractuelles Types de l&apos;UE
                </span>
              </li>
            </ul>
          </div>

          {/* Consent checkbox */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                disabled={loading}
                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                J'accepte que mes conversations contenant potentiellement des données de santé
                soient traitées et stockées de manière chiffrée conformément à la{' '}
                <Link to="/legal/privacy" className="text-blue-600 underline" target="_blank">
                  Politique de confidentialité
                </Link>
                . Je comprends que je peux retirer mon consentement à tout moment via mon profil.
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAccept}
              disabled={!accepted || loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              {loading ? 'Enregistrement...' : "J'accepte et je continue"}
            </button>

            <button
              onClick={handleDecline}
              disabled={loading}
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Je refuse
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            Vous pourrez modifier votre choix à tout moment dans les paramètres de votre compte.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HealthDataConsent;
